/** @file evaluate: the frozen pose entry point, clip sampling and blending through the exact FK chain. */
import type { Evaluate } from '@zilo/pose3d-pose/contract';
import { attachmentPlans } from '@zilo/pose3d-pose/attach/attachmentPlans';
import { sampleAttachments } from '@zilo/pose3d-pose/attach/sampleAttachments';
import { chainPose } from '@zilo/pose3d-pose/chain/chainPose';
import { prepareRig } from '@zilo/pose3d-pose/rig/prepareRig';
import { blendLocals } from '@zilo/pose3d-pose/sample/blendLocals';
import { buildChannels } from '@zilo/pose3d-pose/sample/buildChannels';
import { identityLocals } from '@zilo/pose3d-pose/sample/identityLocals';
import { sampleLocals } from '@zilo/pose3d-pose/sample/sampleLocals';
import { clipFrame } from '@zilo/pose3d-pose/time/clipFrame';

/**
 * Pose of `puppet` at `state` into `out`: `null` -> the rest pose; else the clip sampled at `state.frame` when the host
 * passes it (21.2, no epsilon), else at `state.time`; node frame animations on `params.frameAnimTicks`; the outgoing
 * `blendFrom` clip mixed in by its weight, bound attachment clips (`bindAttachmentClips`) on their own nodes, then the
 * chain with facing, tilt and mirror. The locals stay in `prepareRig(puppet).workLocalsA` for `applyPlants`.
 */
export const evaluate: Evaluate = (puppet, state, params, out) => {
  const rig = prepareRig(puppet);
  const locals = rig.workLocalsA;
  if (state === null) {
    identityLocals(rig, locals, params.frameAnimTicks);
  } else {
    const time = clipFrame(state.clip, state.time, rig.workTimeA, state.frame, state.loops);
    sampleLocals(buildChannels(state.clip, rig), time, locals, params.frameAnimTicks);
    const from = state.blendFrom;
    if (from !== undefined && from.weight > 0) {
      const old = sampleLocals(buildChannels(from.clip, rig), clipFrame(from.clip, from.time, rig.workTimeB), rig.workLocalsB, params.frameAnimTicks);
      blendLocals(old, locals, from.weight, locals);
    }
    const plans = attachmentPlans.get(puppet);
    if (plans !== undefined) sampleAttachments(rig, plans, state.time, locals);
  }
  return chainPose(rig, locals, params, out);
};
